import { Target, Crosshair, Flame } from "lucide-react";

/**
 * DominancePanel
 *
 * Compares home vs away across three live stats that map to "who is
 * dominating the game right now":
 *   - Shots on target
 *   - Shots off target
 *   - Dangerous attacks
 *
 * Also renders a headline "Dominance" bar derived from LMI.
 */
const StatRow = ({ label, Icon, homeVal, awayVal, homeName, awayName }) => {
  const total = Math.max(1, (Number(homeVal) || 0) + (Number(awayVal) || 0));
  const hPct = Math.round(((Number(homeVal) || 0) / total) * 100);
  const aPct = 100 - hPct;
  const leader = hPct > aPct ? "home" : aPct > hPct ? "away" : "tied";
  return (
    <div className="space-y-1.5" data-testid={`dominance-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between text-xs">
        <span className="font-tabular font-bold text-lg">{homeVal ?? 0}</span>
        <span className="eyebrow flex items-center gap-1.5 text-muted-foreground">
          <Icon className="w-3.5 h-3.5" /> {label}
        </span>
        <span className="font-tabular font-bold text-lg">{awayVal ?? 0}</span>
      </div>
      <div className="flex h-2 rounded-full overflow-hidden bg-muted">
        <div
          className={`transition-all duration-500 ${leader === "home" ? "bg-primary" : "bg-muted-foreground/50"}`}
          style={{ width: `${hPct}%` }}
          title={`${homeName} ${hPct}%`}
        />
        <div
          className={`transition-all duration-500 ${leader === "away" ? "bg-primary" : "bg-muted-foreground/50"}`}
          style={{ width: `${aPct}%` }}
          title={`${awayName} ${aPct}%`}
        />
      </div>
    </div>
  );
};

const pick = (stats, ...keys) => {
  for (const k of keys) {
    const v = stats?.[k];
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return 0;
};

export const DominancePanel = ({
  homeStats, awayStats, homeLmi, awayLmi, homeName, awayName,
  status, minute, statsAvailable,
}) => {
  const h = homeStats || {};
  const a = awayStats || {};
  const hSot = pick(h, "shots_on_target", "shots_on_goal");
  const aSot = pick(a, "shots_on_target", "shots_on_goal");
  const hOff = pick(h, "shots_off_target", "shots_off_goal");
  const aOff = pick(a, "shots_off_target", "shots_off_goal");
  const hDa = pick(h, "dangerous_attacks");
  const aDa = pick(a, "dangerous_attacks");
  const hPoss = pick(h, "ball_possession");
  const aPoss = pick(a, "ball_possession");

  const hLmi = typeof homeLmi === "number" ? homeLmi : 0;
  const aLmi = typeof awayLmi === "number" ? awayLmi : 0;
  const total = Math.max(0.0001, hLmi + aLmi);
  const hDom = total > 0 ? Math.round((hLmi / total) * 100) : 50;
  const aDom = 100 - hDom;

  const hasLmi = hLmi > 0 || aLmi > 0;
  const hasShots = [hSot, aSot, hOff, aOff].some((v) => Number(v) > 0);
  const hasPoss = Number(hPoss) > 0 || Number(aPoss) > 0;
  const hasDa = Number(hDa) > 0 || Number(aDa) > 0;
  const anyData = hasLmi || hasShots || hasPoss || hasDa;

  // Derive a friendly "waiting" message
  const mins = Number(minute) || 0;
  const preKickoff = (status || "").toUpperCase() === "NS" || mins < 1;
  const providerHasNoStats = statsAvailable === false && !preKickoff;

  return (
    <div className="nd-card p-5 sm:p-6 space-y-5" data-testid="dominance-panel">
      <div className="flex items-center justify-between">
        <span className="eyebrow">Who's dominating</span>
        <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">
          <span
            className={hDom >= aDom ? "text-primary" : ""}
            data-testid="dominance-home-pct"
          >
            {homeName}
          </span>
          <span className="mx-2">vs</span>
          <span
            className={aDom > hDom ? "text-primary" : ""}
            data-testid="dominance-away-pct"
          >
            {awayName}
          </span>
        </div>
      </div>

      {!anyData ? (
        <p className="text-sm text-muted-foreground" data-testid="dominance-empty-note">
          {preKickoff
            ? "Match hasn't kicked off yet — stats will appear once play starts."
            : providerHasNoStats
            ? "Live stats are not broadcast for this match by our data provider — the dominance panel will stay blank for this fixture."
            : "Awaiting first live stats from the provider… (usually arrives 2-5 min after kickoff)"}
        </p>
      ) : (
        <>
          {/* Headline dominance bar */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-xs">
              <span className="font-tabular font-bold text-2xl text-foreground">{hDom}%</span>
              <span className="eyebrow">LMI dominance</span>
              <span className="font-tabular font-bold text-2xl text-foreground">{aDom}%</span>
            </div>
            <div className="flex h-3 rounded-full overflow-hidden bg-muted" data-testid="dominance-bar">
              <div
                className="bg-primary transition-all duration-700"
                style={{ width: `${hDom}%` }}
              />
              <div
                className="bg-muted-foreground/40 transition-all duration-700"
                style={{ width: `${aDom}%` }}
              />
            </div>
          </div>

          <div className="border-t border-border pt-4 space-y-4">
            <StatRow
              label="Shots on target"
              Icon={Target}
              homeVal={hSot}
              awayVal={aSot}
              homeName={homeName}
              awayName={awayName}
            />
            <StatRow
              label="Shots off target"
              Icon={Crosshair}
              homeVal={hOff}
              awayVal={aOff}
              homeName={homeName}
              awayName={awayName}
            />
            <StatRow
              label="Dangerous attacks"
              Icon={Flame}
              homeVal={hDa}
              awayVal={aDa}
              homeName={homeName}
              awayName={awayName}
            />
            {hasPoss && (
              <StatRow
                label="Ball possession %"
                Icon={Target}
                homeVal={hPoss}
                awayVal={aPoss}
                homeName={homeName}
                awayName={awayName}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default DominancePanel;
